// Shared kit for the gun models (gunmodels.ts): primitive helpers in the gun frame, the concept
// palette, the finish recolour, and the per-frame action state every model poses its moving
// parts from.
//
// Gun frame (same as the old placeholders and the baked class guns): origin at the grip where the
// gun hand closes, barrel down +Z, top +Y, metres. The geometry goes through the robots' print kit
// (render/robots/kit.ts PartBuilder + Rig): ink hull baked in, rust and edge chips per sub, decals
// from the shared atlas, glow lit per part. One rigid part = one slot; the model writes each slot's
// matrix every frame (TypeBatch.part), so slides, cylinders, bolts and hinges move without bones.

import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { T } from '../render/robots/kit';
import { PAL } from '../render/palette';
import { finishPalette } from '../progress/finishes';
import { Finish } from '../../sim/progression';

const { BoxGeometry: Box, CylinderGeometry: Cyl, TorusGeometry: Tor, LatheGeometry: Lathe, SphereGeometry: Sph } = THREE;

// ---------------------------------------------------------------- the concept palette
// Muted inks on cream, as the Midjourney sheets print them (weapons/concepts/*.png). Saturated red
// stays off the bodies: the art guide keeps red for gameplay warnings.
export const C = {
  cream: 0xe6d6ac, cream2: 0xd3bf8e, paper: 0xc9a46e, mint: 0x9fc29a, teal: 0x5d8c84, tealDk: 0x3f655f,
  orange: 0xd9662b, burnt: 0xb4532a, plum: 0x6c3d44, plumDk: 0x48282d, maroon: 0x7a3a33,
  brass: 0xc08a3e, brassLt: 0xdcae62, bronze: 0x96703f, copper: 0xb0705a,
  gunmetal: 0x433d3c, steel: 0xa2998c, steelDk: 0x6c665e, dark: 0x2a2420, ink: PAL.inkDark,
  walnut: 0x6e4529, lacquer: 0x4f2c26, ivory: 0xe8d9b2, mustard: 0xd29c34, seagreen: 0x7fa58a, olive: 0x8c8746,
  grey: 0x8b8a82, cord: 0x5a3a26, glass: 0xa9c9c0,
  // element inks: a printed body colour and a glow colour
  fire: 0xd8662a, fireGlow: 0xff8a3a, ice: 0x93c8de, iceGlow: 0x9fe6ff, shock: 0xe2c03a, shockGlow: 0xfff07a,
  acid: 0x8fbf3a, acidGlow: 0xb6ff4a, mag: 0x8a52d6, magGlow: 0xc08aff, tealGlow: 0x6fffe0, orangeGlow: 0xffa04a,
} as const;

// ---------------------------------------------------------------- finishes
// Every model paints from roles. The default finish is the concept's own paint job; the Service
// Record finishes swap the metal roles (finishes.ts) and, for the loud ones, the enamel too.
export interface Paint {
  metal: number; steel: number; brass: number; wood: number; grip: number; cream: number;
  /** enamel: the concept's signature colours pass through this */
  enamel(c: number): number;
}
const _a = new THREE.Color(), _b = new THREE.Color();
const mix = (c: number, to: number, k: number) => _a.set(c).lerp(_b.set(to), k).getHex();
export function paintFor(finish: number): Paint {
  const k = finishPalette(finish);
  const base = finish === Finish.Blued || finish === 0;
  const enamel = (c: number) => {
    switch (finish) {
      case Finish.Nickel: return mix(c, 0xd8dcdc, 0.55);
      case Finish.Blackout: return mix(c, 0x2c2a2c, 0.82);
      case Finish.Gold: return mix(c, 0xd9a440, 0.6);
      case Finish.Engraved: return mix(c, 0xc9a464, 0.3);
      default: return c;
    }
  };
  return base
    ? { metal: C.gunmetal, steel: C.steel, brass: C.brass, wood: C.walnut, grip: C.cord, cream: C.cream, enamel }
    : { metal: k.metal, steel: k.steel, brass: k.brass, wood: k.wood, grip: k.grip, cream: k.cream, enamel };
}

// ---------------------------------------------------------------- primitives (gun frame)
export const box = (w: number, h: number, d: number, x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0) =>
  T(new Box(w, h, d), x, y, z, rx, ry, rz);
/** rounded box (wears at its edges: the kit chips enamel where the smooth normal turns) */
export const rb = (w: number, h: number, d: number, r: number, x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0) =>
  T(new RoundedBoxGeometry(w, h, d, 2, Math.min(r, w / 2 - 1e-4, h / 2 - 1e-4, d / 2 - 1e-4)), x, y, z, rx, ry, rz);
/** cylinder along +Z from z0 to z1 (r0 at z0, r1 at z1) */
export function cz(r0: number, r1: number, z0: number, z1: number, seg: number, x = 0, y = 0, open = false) {
  const g = new Cyl(r1, r0, z1 - z0, seg, 1, open);
  g.rotateX(Math.PI / 2);
  g.translate(x, y, (z0 + z1) / 2);
  return g;
}
/** cylinder along X (centre x, length len) */
export function cx(r: number, len: number, seg: number, x: number, y: number, z: number, r2 = r) {
  const g = new Cyl(r2, r, len, seg);
  g.rotateZ(-Math.PI / 2);
  g.translate(x, y, z);
  return g;
}
/** cylinder along Y */
export const cy = (r: number, h: number, seg: number, x: number, y: number, z: number, r2 = r) => T(new Cyl(r2, r, h, seg), x, y, z);
/** ring around the Z axis (a band on a barrel) */
export const ringZ = (R: number, r: number, z: number, x = 0, y = 0, seg = 12, arc = Math.PI * 2) => T(new Tor(R, r, 4, seg, arc), x, y, z);
/** ring around the X axis */
export const ringX = (R: number, r: number, x: number, y: number, z: number, seg = 12, arc = Math.PI * 2) => T(new Tor(R, r, 4, seg, arc), x, y, z, 0, Math.PI / 2, 0);
/** ring around the Y axis */
export const ringY = (R: number, r: number, x: number, y: number, z: number, seg = 12) => T(new Tor(R, r, 4, seg), x, y, z, Math.PI / 2);
/** body of revolution along +Z: profile [radius, z] pairs */
export function lathe(prof: [number, number][], seg: number, x = 0, y = 0, z = 0) {
  // the lathe faces outward only when the profile runs up the axis
  const p = prof[0][1] > prof[prof.length - 1][1] ? [...prof].reverse() : prof;
  const g = new Lathe(p.map(([r, zz]) => new THREE.Vector2(Math.max(r, 1e-4), zz)), seg);
  g.rotateX(Math.PI / 2);
  g.translate(x, y, z);
  return g;
}
export const ball = (r: number, x: number, y: number, z: number, ws = 10, hs = 7, sx = 1, sy = 1, sz = 1) => T(new Sph(r, ws, hs), x, y, z, 0, 0, 0, sx, sy, sz);
/** a stick from a to b */
export function rod(a: [number, number, number], b: [number, number, number], r: number, seg = 6, r2 = r) {
  const A = new THREE.Vector3(...a), B = new THREE.Vector3(...b);
  const d = B.clone().sub(A);
  const g = new Cyl(r2, r, d.length(), seg);
  g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.clone().normalize()));
  const m = A.add(B).multiplyScalar(0.5);
  g.translate(m.x, m.y, m.z);
  return g;
}
/** a decal quad facing +X (side = 1) or -X (side = -1) at gun-frame x, y, z */
export const sideDecal = (w: number, h: number, side: 1 | -1, x: number, y: number, z: number, rz = 0) =>
  T(new THREE.PlaneGeometry(w, h), x, y, z, 0, side > 0 ? -Math.PI / 2 : Math.PI / 2, rz);
/** a decal quad facing up */
export const topDecal = (w: number, h: number, x: number, y: number, z: number, rz = 0) => T(new THREE.PlaneGeometry(w, h), x, y, z, -Math.PI / 2, 0, rz);
/** flip a geometry inside out (the inner wall of an open tube or cone) */
export function inward(g0: THREE.BufferGeometry) {
  const g = g0.index ? g0.toNonIndexed() : g0;
  for (const name of Object.keys(g.attributes)) {
    const at = g.attributes[name], a = at.array as Float32Array, w = at.itemSize;
    for (let t = 0; t < a.length; t += w * 3) for (let k = 0; k < w; k++) { const v = a[t + w + k]; a[t + w + k] = a[t + 2 * w + k]; a[t + 2 * w + k] = v; }
  }
  const n = g.attributes.normal?.array as Float32Array | undefined;
  if (n) for (let i = 0; i < n.length; i++) n[i] = -n[i];
  return g;
}
/** a side-profile solid (stocks, grips): a smooth closed outline through [z, y] points in the gun
 *  frame, `w` thick across X, centred on x, with rounded edges */
export function side(pts: [number, number][], w: number, x = 0, bevel = 0.005, n = 48) {
  const c = new THREE.CatmullRomCurve3(pts.map(([z, y]) => new THREE.Vector3(z, y, 0)), true, 'centripetal');
  const shape = new THREE.Shape(c.getPoints(n).map((v) => new THREE.Vector2(v.x, v.y)));
  const b = Math.min(bevel, w * 0.45);
  const g = new THREE.ExtrudeGeometry(shape, { depth: Math.max(1e-4, w - 2 * b), bevelEnabled: true, bevelThickness: b, bevelSize: b * 0.8, bevelSegments: 2, curveSegments: 4 });
  g.translate(0, 0, -(w - 2 * b) / 2);
  g.rotateY(-Math.PI / 2);
  g.translate(x, 0, 0);
  return g;
}
/** a straight-edged side-profile solid: the polygon [z, y] (corners kept sharp, edges lightly
 *  bevelled), `w` thick across X, centred on x */
export function slab(pts: [number, number][], w: number, x = 0, bevel = 0.0012) {
  const shape = new THREE.Shape(pts.map(([z, y]) => new THREE.Vector2(z, y)));
  const b = Math.min(bevel, w * 0.3);
  const g = new THREE.ExtrudeGeometry(shape, { depth: Math.max(1e-4, w - 2 * b), bevelEnabled: b > 0, bevelThickness: b, bevelSize: b, bevelSegments: 1, curveSegments: 6 });
  g.translate(0, 0, -(w - 2 * b) / 2);
  g.rotateY(-Math.PI / 2);
  g.translate(x, 0, 0);
  return g;
}

/**
 * Author a gun in its concept sheet's pixels (weapons/concepts/*.png, muzzle to the right): `ox, oy`
 * is the pixel where the gun hand closes (the gun-frame origin) and `s` metres per pixel. Pixel x
 * runs down the barrel (+Z), pixel y runs down the sheet (-Y). Thickness across X stays in metres.
 */
export class Sheet {
  constructor(readonly ox: number, readonly oy: number, readonly s: number) {}
  z = (px: number) => (px - this.ox) * this.s;
  y = (py: number) => (this.oy - py) * this.s;
  l = (px: number) => px * this.s;
  /** gun-frame point at pixel (px, py), x across */
  p = (px: number, py: number, x = 0): [number, number, number] => [x, this.y(py), this.z(px)];
  /** a block over the pixel rect, w thick, edge radius r */
  blk(x0: number, y0: number, x1: number, y1: number, w: number, r = 0.0018, x = 0) {
    return rb(w, Math.abs(y1 - y0) * this.s, Math.abs(x1 - x0) * this.s, r, x, this.y((y0 + y1) / 2), this.z((x0 + x1) / 2));
  }
  /** a sharp polygon through pixel points */
  slab(pts: [number, number][], w: number, x = 0, bevel = 0.0012) { return slab(pts.map(([a, b]) => [this.z(a), this.y(b)]), w, x, bevel); }
  /** a smooth closed curve through pixel points */
  curve(pts: [number, number][], w: number, x = 0, bevel = 0.0018, n = 48) { return side(pts.map(([a, b]) => [this.z(a), this.y(b)]), w, x, bevel, n); }
  /** a round tube down the barrel axis from pixel x0 to x1 on pixel row py, radius in pixels */
  tube(x0: number, x1: number, py: number, rpx: number, seg = 14, x = 0, rpx1 = rpx) { return cz(rpx * this.s, rpx1 * this.s, this.z(x0), this.z(x1), seg, x, this.y(py)); }
  /** a disc/drum across X centred on pixel (px, py), radius in pixels, w thick */
  drum(px: number, py: number, rpx: number, w: number, seg = 18, x = 0) { return cx(rpx * this.s, w, seg, x, this.y(py), this.z(px)); }
}

/** a pistol trigger guard: a half ring in the YZ plane under the frame, centred at (y, z) */
export const guard = (y: number, z: number, R = 0.022, r = 0.0035) => T(new Tor(R, r, 4, 10, Math.PI), 0, y, z, 0, Math.PI / 2, Math.PI);

// ---------------------------------------------------------------- the action state
/** What a gun is doing this frame: GunRack fills it from the cowboy view and the shot history. */
export interface GunAct {
  /** seconds, monotonic */
  t: number;
  /** seconds since the last shot (large if none) */
  shotAge: number;
  /** shots fired with this gun in hand (counts up per shot; the revolver turns a chamber per shot) */
  shots: number;
  /** rounds in the burst in progress (the Pulse Rifle's three cells) */
  burst: number;
  /** reload: weight 0..1, phase 0..1 within one cycle, which cycle (round-by-round loaders repeat) */
  reload: number; reloadU: number; reloadN: number;
  /** Auto MG spin 0..1 (exact for your own cowboy, from the shot cadence for the others) */
  spin: number;
  /** barrel turns so far (revolutions, accumulated from spin) */
  turn: number;
  /** rounds in the gun (exact for your own cowboy, counted from shots and reloads for the others) */
  loaded: number;
  /** Duel Blaster charge 0..1 (your own cowboy; 0 for the others) */
  charge: number;
  /** aiming down sights 0..1 */
  ads: number;
  /** this gun is the off-hand twin (akimbo) */
  twin: boolean;
}
export function restAct(): GunAct {
  return { t: 0, shotAge: 99, shots: 0, burst: 0, reload: 0, reloadU: 0, reloadN: 0, spin: 0, turn: 0, loaded: 99, charge: 0, ads: 0, twin: false };
}

/** where a model writes its parts each frame */
export interface PartSink { part(slot: number, m: THREE.Matrix4, glow: number): void }

// ---------------------------------------------------------------- part matrices (allocation-free)
const _m = new THREE.Matrix4(), _r = new THREE.Matrix4(), _t = new THREE.Matrix4();
const _q = new THREE.Quaternion(), _ax = new THREE.Vector3();
export const IDENT = new THREE.Matrix4();
export const HIDDEN = new THREE.Matrix4().makeScale(0, 0, 0);
/** rotate `ang` about the axis (ax ay az) through the pivot (px py pz), then translate (tx ty tz) */
export function about(px: number, py: number, pz: number, ax: number, ay: number, az: number, ang: number, tx = 0, ty = 0, tz = 0, out = _m) {
  _q.setFromAxisAngle(_ax.set(ax, ay, az).normalize(), ang);
  _r.makeRotationFromQuaternion(_q);
  _t.makeTranslation(-px, -py, -pz);
  out.multiplyMatrices(_r, _t);
  _t.makeTranslation(px + tx, py + ty, pz + tz);
  return out.premultiply(_t);
}
/** a plain move */
export const moved = (x: number, y: number, z: number, out = _m) => out.makeTranslation(x, y, z);
/** b after a (both in the gun frame): the returned matrix is a scratch, use it before the next call */
const _c2 = new THREE.Matrix4();
export function chain(a: THREE.Matrix4, b: THREE.Matrix4) { return _c2.multiplyMatrices(b, a); }

// ---------------------------------------------------------------- easing
export const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
export const smooth = (x: number) => { const c = clamp01(x); return c * c * (3 - 2 * c); };
/** 0 -> 1 over [a, b] */
export const ramp = (u: number, a: number, b: number) => smooth((u - a) / (b - a));
/** 0 -> 1 -> 0: up over [a, b], held, down over [c, d] */
export const pulse = (u: number, a: number, b: number, c: number, d: number) => ramp(u, a, b) * (1 - ramp(u, c, d));
/** a quick out-and-back kick that starts at age 0 and settles by `len` seconds */
export const kick = (age: number, len: number) => (age < 0 || age > len ? 0 : Math.sin((age / len) * Math.PI) * (1 - age / len) * 1.6);
